// Compte et réglages (hors maquette, composants du handoff) : mes tableaux (modifier, supprimer),
// notifications (topic ntfy, chat Telegram, envoi d'essai), mot de passe, thème, administration.
import { PASSWORD_RULES, type ChannelDto, type NotificationChannel, type TableDto } from '@crenow/shared';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../api.ts';
import { CATEGORY_LABELS } from '../format.ts';
import { useAccount, useSession } from '../session.tsx';
import { useTheme } from '../theme.ts';
import { Empty, Field, FormError, Loading, Segmented, TabBar, useFlash, useResource } from '../ui.tsx';

const CHANNELS: { channel: NotificationChannel; label: string; placeholder: string; help: string }[] = [
  { channel: 'ntfy', label: 'ntfy', placeholder: 'mon-topic-crenow', help: 'Topic ntfy auquel votre téléphone est abonné.' },
  { channel: 'telegram', label: 'Telegram', placeholder: '123456789', help: 'Identifiant de la discussion avec le bot (ou @canal).' },
];

export function AccountPage() {
  const account = useAccount();
  const { logout } = useSession();
  const [theme, setTheme] = useTheme();
  return (
    <div className="screen">
      <header className="screen-head compact">
        <div className="head-row">
          <h1 className="h1">Compte</h1>
          <span className="meta">{account.login}</span>
        </div>
      </header>
      <main className="screen-body account">
        <Tables />
        <Channels />
        <section className="account-part">
          <h2 className="section-label">Apparence</h2>
          <Segmented
            label="Thème"
            value={theme}
            onChange={setTheme}
            options={[
              { value: 'system', label: 'Système' },
              { value: 'light', label: 'Clair' },
              { value: 'dark', label: 'Sombre' },
            ]}
          />
        </section>
        <Password />
        {account.role === 'admin' && (
          <Link to="/admin" className="btn btn-secondary btn-48">
            Administration
          </Link>
        )}
        <button type="button" className="btn btn-danger btn-48" onClick={() => void logout()}>
          Se déconnecter
        </button>
      </main>
      <TabBar />
    </div>
  );
}

function Tables() {
  const { data, error, reload } = useResource<{ tables: TableDto[] }>('/tables');
  const [problem, setProblem] = useState<string | null>(null);

  async function remove(table: TableDto) {
    if (!window.confirm(`Supprimer le tableau « ${table.name} » ? Ses fenêtres et ses liens disparaissent.`)) return;
    try {
      await api('DELETE', `/tables/${table.id}`);
      await reload();
    } catch (caught) {
      setProblem(errorMessage(caught));
    }
  }

  return (
    <section className="account-part">
      <h2 className="section-label">Mes tableaux</h2>
      <FormError error={error ?? problem} />
      {!data && !error && <Loading />}
      {data && data.tables.length === 0 && (
        <Empty>
          <span>Pas encore de tableau.</span>
          <Link to="/creer" className="btn btn-primary btn-44">
            Créer un tableau
          </Link>
        </Empty>
      )}
      {data && data.tables.length > 0 && (
        <div className="list">
          {data.tables.map((table) => (
            <div key={table.id} className="row table-row">
              <span className="row-main">
                <span>{table.name}</span>
                <span>
                  {[...new Set(table.profiles.map((profile) => CATEGORY_LABELS[profile.category]))].join(', ')} · {table.profiles.length} profil{table.profiles.length > 1 ? 's' : ''} ·{' '}
                  {table.locations.length} lieu{table.locations.length > 1 ? 'x' : ''}
                </span>
              </span>
              <Link to={`/creer?tableau=${table.id}`} className="btn btn-secondary btn-34">
                Modifier
              </Link>
              <button type="button" className="btn btn-danger btn-34" aria-label={`Supprimer ${table.name}`} onClick={() => void remove(table)}>
                Supprimer
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Channels() {
  const { data, error, reload } = useResource<{ channels: ChannelDto[]; available: NotificationChannel[] }>('/account/channels');
  return (
    <section className="account-part">
      <h2 className="section-label">Notifications</h2>
      <p className="help">Un message à l’ouverture d’une fenêtre, à son décalage de plus d’un jour et à sa fermeture.</p>
      <FormError error={error} />
      {!data && !error && <Loading />}
      {data &&
        CHANNELS.map((config) => (
          <ChannelCard key={config.channel} config={config} current={data.channels.find((channel) => channel.channel === config.channel) ?? null} available={data.available.includes(config.channel)} onSaved={reload} />
        ))}
    </section>
  );
}

function ChannelCard({ config, current, available, onSaved }: { config: (typeof CHANNELS)[number]; current: ChannelDto | null; available: boolean; onSaved: () => Promise<void> }) {
  const [target, setTarget] = useState(current?.target ?? '');
  const [error, setError] = useState<string | null>(null);
  const [done, flash] = useFlash(1800);

  async function run(task: () => Promise<unknown>, message: string) {
    setError(null);
    try {
      await task();
      await onSaved();
      flash(message);
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    await run(() => api('PUT', `/account/channels/${config.channel}`, { target, enabled: current?.enabled ?? true }), 'Enregistré');
  }

  return (
    <form className="card channel" onSubmit={save}>
      <div className="head-row">
        <span className="card-title">{config.label}</span>
        {!available ? (
          <span className="tag off">NON CONFIGURÉ SUR LE SERVEUR</span>
        ) : current ? (
          <button
            type="button"
            className={`switch-row`}
            role="switch"
            aria-checked={current.enabled}
            aria-label={`Notifications ${config.label}`}
            onClick={() => void run(() => api('PUT', `/account/channels/${config.channel}`, { target: current.target, enabled: !current.enabled }), current.enabled ? 'Coupé' : 'Activé')}
          >
            <span className={`switch${current.enabled ? ' on' : ''}`} aria-hidden="true">
              <span />
            </span>
          </button>
        ) : null}
      </div>
      <Field label="Destination" className="input input-44" placeholder={config.placeholder} value={target} onChange={(event) => setTarget(event.target.value)} />
      <p className="help">{config.help}</p>
      <FormError error={error} />
      <div className="channel-actions">
        <button className="btn btn-primary btn-38" disabled={!target.trim() || target === current?.target}>
          Enregistrer
        </button>
        {current && available && (
          <button type="button" className="btn btn-secondary btn-38" onClick={() => void run(() => api('POST', `/account/channels/${config.channel}/test`), 'Essai envoyé')}>
            Envoyer un essai
          </button>
        )}
        {current && (
          <button type="button" className="btn btn-danger btn-38" onClick={() => void run(() => api('DELETE', `/account/channels/${config.channel}`).then(() => setTarget('')), 'Retiré')}>
            Retirer
          </button>
        )}
        <span className="flash" role="status">
          {done ? `${done} ✓` : ''}
        </span>
      </div>
    </form>
  );
}

function Password() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const failing = PASSWORD_RULES.filter((rule) => !rule.test(next)).map((rule) => rule.label.toLocaleLowerCase('fr-FR'));

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await api('PUT', '/account/password', { currentPassword: current, newPassword: next });
      setDone(true);
      setOpen(false);
      setCurrent('');
      setNext('');
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  return (
    <section className="account-part">
      <h2 className="section-label">Mot de passe</h2>
      {done && (
        <p className="help" role="status">
          Mot de passe changé. Vos autres sessions sont fermées.
        </p>
      )}
      {!open ? (
        <button type="button" className="btn btn-secondary btn-44" onClick={() => setOpen(true)}>
          Changer le mot de passe
        </button>
      ) : (
        <form className="card" onSubmit={submit}>
          <Field label="Mot de passe actuel" type="password" autoComplete="current-password" className="input input-44" required value={current} onChange={(event) => setCurrent(event.target.value)} />
          <Field
            label="Nouveau mot de passe"
            type="password"
            autoComplete="new-password"
            className="input input-44"
            required
            value={next}
            onChange={(event) => setNext(event.target.value)}
            error={next && failing.length > 0 ? `Manque : ${failing.join(', ')}` : null}
          />
          <FormError error={error} />
          <div className="channel-actions">
            <button className="btn btn-primary btn-38" disabled={failing.length > 0 || !current}>
              Enregistrer
            </button>
            <button type="button" className="btn btn-secondary btn-38" onClick={() => setOpen(false)}>
              Annuler
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
